import React, { useState } from 'react';
import { FiAlertTriangle, FiCalendar } from 'react-icons/fi';

/**
 * 🔴 Component สำหรับแสดงสถานะ Token หมดอายุ
 * ตามขั้นตอนที่ผู้ใช้กำหนด:
 * 
 * Status: Expired 🔴
 * This broker connection has expired on 12/07/2025.
 * → Please reconnect to continue trading.
 * [Disconnect]
 */
const ExpiredTokenCard = ({
  integration,
  onRemove,
  onReconnect,
  brokerDisplayName
}) => {
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const isNeedsReauth = integration.status === 'needs_reauth';
  const statusLabel = isNeedsReauth ? 'Reconnect Required' : 'Expired';
  const reauthReason = integration.metadata?.needsReauthReason;
  // Either action locks out the other. OAuth redirect is not instantaneous;
  // we don't want the user clicking Disconnect mid-flight, or Reconnect while
  // a disconnect network call is still in flight.
  const busy = isRemoving || isReconnecting;

  const handleReconnectClick = async () => {
    if (busy || !onReconnect) return;
    setIsReconnecting(true);
    try {
      await onReconnect(integration);
    } catch (err) {
      console.error('Reconnect failed:', err);
    } finally {
      // Always reset. On a successful redirect, `window.location.href` navigates
      // away before React re-renders so the user never sees the button restored.
      // On a non-throwing failure (handler showed an alert and returned), this
      // avoids the button getting permanently stuck on "Redirecting...".
      setIsReconnecting(false);
    }
  };

  // ได้วันที่หมดอายุ
  const getExpiredDate = () => {
    // Check multiple possible locations for expiry date (including expires_at from backend)
    const expiredDate = integration.credentials?.expires_at ||
                       integration.credentials?.expiresAt ||
                       integration.credentials?.sessionExpiration ||
                       integration.metadata?.expirationDate;

    console.log('🔍 ExpiredTokenCard debugging:', {
      broker: integration.broker,
      status: integration.status,
      expiredDate,
      credentials: integration.credentials,
      metadata: integration.metadata
    });

    if (!expiredDate) return 'Unknown';

    try {
      const date = new Date(expiredDate);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
      }
      return 'Invalid Date';
    } catch (error) {
      return 'Invalid Date';
    }
  };


  // จัดการ Remove
  const handleRemove = async () => {
    if (!showRemoveConfirm) {
      setShowRemoveConfirm(true);
      return;
    }

    setIsRemoving(true);
    try {
      await onRemove(integration.id, brokerDisplayName);
      setShowRemoveConfirm(false);
    } catch (error) {
      console.error('Failed to remove integration:', error);
      alert('Failed to remove broker connection. Please try again.');
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <div className="bg-red-900/20 rounded-lg p-4 border border-red-800/50 mb-4">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-900/30 rounded-lg flex items-center justify-center">
            <FiAlertTriangle className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h3 className="text-white font-medium">{brokerDisplayName}</h3>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-900 text-red-300">
                <span className="w-2 h-2 bg-red-400 rounded-full mr-1.5"></span>
                Status: {statusLabel}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Expired Message */}
      <div className="mb-4 p-3 bg-red-900/10 rounded border border-red-800/30">
        <div className="flex items-start gap-2">
          <FiCalendar className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            {isNeedsReauth ? (
              <>
                <p className="text-red-300 font-medium">
                  This broker connection needs to be re-authenticated.
                </p>
                <p className="text-red-400 mt-1">
                  → Please reconnect to resume trading.
                </p>
              </>
            ) : (
              <>
                <p className="text-red-300 font-medium">
                  This broker connection has expired on {getExpiredDate()}.
                </p>
                <p className="text-red-400 mt-1">
                  → Please reconnect to continue trading.
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* User Details */}
      <div className="mb-4 space-y-2 text-xs text-gray-400">
        <div className="flex justify-between">
          <span>Username:</span>
          <span className="text-gray-300">
            {integration.credentials?.userUsername ||
             integration.credentials?.userName ||
             integration.metadata?.connectedUsername ||
             integration.username ||
             'N/A'}
          </span>
        </div>
        <div className="flex justify-between">
          <span>Account Type:</span>
          <span className="text-gray-300">
            {integration.metadata?.accountType || 'N/A'}
          </span>
        </div>
        <div className="flex justify-between">
          <span>Connected:</span>
          <span className="text-gray-300">
            {integration.connectionDate?.seconds
              ? new Date(integration.connectionDate.seconds * 1000).toLocaleDateString('en-GB')
              : integration.metadata?.lastLogin
              ? typeof integration.metadata.lastLogin === 'string'
                ? new Date(integration.metadata.lastLogin).toLocaleDateString('en-GB')
                : new Date(integration.metadata.lastLogin.seconds * 1000).toLocaleDateString('en-GB')
              : 'N/A'}
          </span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        {isNeedsReauth && onReconnect && (
          <button
            onClick={handleReconnectClick}
            disabled={busy}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-orange-600 text-white hover:bg-orange-700"
          >
            {isReconnecting ? (
              <>
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Redirecting...
              </>
            ) : (
              <>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reconnect
              </>
            )}
          </button>
        )}
        <button
          onClick={handleRemove}
          disabled={busy}
          className={`flex-1 flex items-center justify-center gap-2 px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
            showRemoveConfirm
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-red-600 text-white hover:bg-red-700'
          }`}
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          {isRemoving ? (
            <>
              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Removing...
            </>
          ) : showRemoveConfirm ? (
            'Confirm Disconnect'
          ) : (
            'Disconnect'
          )}
        </button>
      </div>
      {isNeedsReauth && reauthReason && (
        <p className="text-xs text-gray-500 mt-2">Reason: {reauthReason}</p>
      )}

      {/* Disconnect Confirmation */}
      {showRemoveConfirm && (
        <div className="mt-3 p-3 bg-yellow-900/20 border border-yellow-800/50 rounded-lg">
          <p className="text-yellow-300 text-sm font-medium">
            ⚠️ Confirm Disconnection
          </p>
          <p className="text-yellow-400 text-xs mt-1">
            Are you sure you want to disconnect this expired broker connection? You will need to reconnect to continue trading.
          </p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => setShowRemoveConfirm(false)}
              className="text-xs px-3 py-1 bg-gray-600 text-gray-200 rounded hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpiredTokenCard; 